<template>
    <div
        ref="dropzone"
        class="dropzone w-100 d-flex flex-column position-relative"
        :class="[isDragging ? 'active' : '', $slots.default ? 'has-slot' : '', disabled ? 'disabled' : '']"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @dragend="isDragging = false"
        @drop="drop"
    >
        <div
            class="dropzone-drag flex-grow-1 h-100 w-100 d-flex justify-content-center align-items-center bg-info bg-opacity-10 position-absolute"
            style="z-index: 1050; pointer-events: none"
            :class="{ 'fade-in-fast': isDragging }"
            v-if="isDragging"
        >
            <div class="d-flex flex-column gap-3 text-center">
                <h1 class="display-1"><IconComp icon="fa-cloud-arrow-down"></IconComp></h1>
                <div>{{ $tm('dropzone.releasetodrop') }}</div>
            </div>
        </div>
        <div
            class="dropzone-content flex-grow-1 h-100 w-100 d-flex justify-content-center align-items-center"
            :class="{ 'opacity-25': isDragging }"
            @click="disableClickForFilePicker === true ? () => {} : fileInput?.click()"
        >
            <input
                :accept="props.accept ?? '*'"
                type="file"
                :multiple="props.multiple"
                name="file"
                :id="guid"
                ref="fileInput"
                :disabled="props.disabled"
                @change="onChange"
            />

            <template v-if="$slots.default">
                <slot></slot>
            </template>
            <template v-else>
                <div
                    v-if="showFiles === true && files.length"
                    class="mt-3 d-flex flex-wrap gap-3 justify-content-center align-items-center pb-3"
                >
                    <div
                        class="file-man-box border p-2"
                        v-for="file in files"
                        :key="file.name"
                    >
                        <ButtonComp
                            class="position-absolute top-0 end-0"
                            button-type="Secondary"
                            icon="fa-xmark"
                            @click="removeFile($event, file)"
                        />
                        <div class="file-img-box p-4">
                            <DisplayMimeType
                                :value="file.type"
                                :showIcon="true"
                                :showText="false"
                                class="fa-2xl justify-content-center"
                            />
                        </div>
                        <div class="file-man-title">
                            <p
                                class="mb-0 text-truncate"
                                :title="file.name"
                            >
                                {{ file.name }}
                            </p>
                            <p class="mb-0 small">
                                <small><DisplayByteSize :value="file.size" /></small>
                            </p>
                        </div>
                    </div>
                </div>
                <template v-else>
                    <div
                        class="d-flex flex-column gap-3 text-center align-self-center"
                        v-if="!isDragging"
                    >
                        <h1 class="display-1"><IconComp icon="fa-cloud-arrow-up"></IconComp></h1>
                        <div>{{ $tm('dropzone.drophere') }}</div>
                    </div>
                </template>
            </template>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { IconComp, ButtonComp, DisplayByteSize, DisplayMimeType } from '@/components';
import { randomstring } from '@/utilities/utils';
import { useHelper } from '@/composeables';

// The HTML element that is the dropzone
const dropzone = ref<HTMLDivElement | null>(null);

const fileInput = ref<HTMLInputElement | null>(null);
const isDragging = ref(false);
const files = ref<File[]>([]);

const guid = randomstring('dz-', '');

const props = withDefaults(
    defineProps<{
        /**
         * What filetypes to accept. Default is all files
         * @example 'image/png, image/jpeg', 'application/pdf', 'application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
         * @default '*'
         */
        accept?: string;

        /**
         * Allow multiple files to be uploaded
         * @default false
         */
        multiple?: boolean;

        /**
         * Show the files that have been uploaded
         * @default false
         */
        showFiles?: boolean;

        /**
         * Disable the dropzone
         * @default false
         */
        disabled?: boolean;

        /**
         * This removes the possibility of clicking the dropzone to open the file picker
         * @default false
         */
        disableClickForFilePicker?: boolean;
    }>(),
    {
        accept: '*',
        multiple: false,
        showFiles: false,
        disabled: false,
        disableClickForFilePicker: false,
    },
);

/** Emit the change event */
const emit = defineEmits<{
    (e: 'change', files: File[]): void;
}>();

/** Handling file input */
const onChange = (event: Event) => {
    addFiles((event.target as HTMLInputElement).files);
};

/** Handling drops */
const drop = (event: DragEvent) => {
    addFiles(event.dataTransfer!.files);

    isDragging.value = false;

    event.preventDefault();
    event.stopPropagation();
    return false;
};

/** Add a list of files to the list */
const addFiles = (filelist: FileList | null) => {
    if (filelist === null) return;

    for (let i = 0; i < filelist.length; i++) {
        let file = filelist[i];
        files.value.push(file);
    }

    emit('change', files.value);
};

/** Remove a file from the list */
const removeFile = (event: Event, file: File) => {
    files.value = files.value.filter((f) => f !== file);

    emit('change', files.value);

    return useHelper().stopPropagation(event);
};

/** Helper method to remove deault handling when dragging and dropping */
const preventDefaults = (e: Event) => {
    e.preventDefault();
};

/** Prevent default handling for drag and drop events */
const events = ['dragenter', 'dragover', 'dragleave', 'drop'];

/**
 * Resets the component to its initial state
 */
const reset = () => {
    files.value = [];
};

let dropzoneBoundries: DOMRect | null = null;

const onDragOver = (e: DragEvent) => {
    if (isDragging.value) return;
    // We find the boundries of the dropzone

    if (dropzone.value) {
        dropzoneBoundries = dropzone.value.getBoundingClientRect();
    }

    isDragging.value = true;
};

const onDragLeave = (e: DragEvent) => {
    // We check if the mouse is outside the dropzone
    if (dropzoneBoundries) {
        const x = e.clientX;
        const y = e.clientY;

        if (x < dropzoneBoundries.left || x > dropzoneBoundries.right || y < dropzoneBoundries.top || y > dropzoneBoundries.bottom) {
            isDragging.value = false;
        }
    }
};

onMounted(() => {
    events.forEach((eventName) => {
        document.body.addEventListener(eventName, preventDefaults);
    });
});

onUnmounted(() => {
    events.forEach((eventName) => {
        document.body.removeEventListener(eventName, preventDefaults);
    });
});

defineExpose({
    /**
     * Reset the filelist
     */
    reset,

    /**
     * Opens the file picker
     */
    openFilePicker: () => {
        if (fileInput.value) {
            fileInput.value.click();
        }
    },
});
</script>
